<script setup>
import { useRoute } from 'vue-router'
import CameraInput from '@/components/inputs/CameraInput.vue'
import { getImageService } from '@/services/image-service'
import { useInspectionStore } from '@/stores/inspection-store'
import { Log } from '@/modules/util/log'
import { vehicleImageUrl } from '@/modules/util/vehicle-image'
import { ImageResizeManager } from '@/modules/util/imageresizer'
import { mdiClose } from '@mdi/js'
import draggable from 'vuedraggable'
import { HTTP } from '@/services/http'

const store = useInspectionStore()
const imageService = getImageService(store.currentInspection)
const props = defineProps(['field', 'modelValue'])
const emit = defineEmits(['update:modelValue'])
const route = useRoute()

const onImageTaken = async (image) => {
	const blob = await ImageResizeManager.loadAndResizeImage(image.image, props.field.width || 100)
	const url = URL.createObjectURL(blob)
	
	try {
		const res = await imageService.uploadInspection(route.params.id, image.image, props.field.public)
		if (!res) {
			throw new Error('Image upload failed')
		}

		emit('update:modelValue', [
			...(props.modelValue || []).filter(i => i.image !== image.image), 
			{ image: url, uid: res.uid },
		])
	} catch (e) {
		Log.warning('Unexpected error uploading image', e, e.message)
	}
}

const removeImage = (index) => {
	const values = [...props.modelValue]
	const image = values.splice(index, 1)[0]
    
	emit('update:modelValue', values)

	if (!image) {
		return
	}

	if (image.image) {
		URL.revokeObjectURL(image.image)
	}
	if (image.uid) {
		HTTP.deleteCached(image.uid)
	}
}
</script>

<template>
<v-card>
	<v-card-text>
		<CameraInput
			:color="field.color || 'primary'"
			:variant="field.variant || 'elevated'"
			class="w-100"
			:size="field.size || 'x-large'"
			@update="val => onImageTaken({ image: val.url })"
		>
			{{ field.label || 'Add Photo' }}
		</CameraInput>
		<div class="d-flex flex-row flex-wrap mt-4">
			<draggable
				:model-value="modelValue"
				item-key="imagename"
				class="d-flex flex-row flex-wrap"
				style="gap: 8px"
				:animation="200"
				@update:model-value="val => emit('update:modelValue', val)"
			>
				<template #item="{ element, index }">
					<div class="image-container">
						<img
							:src="element.image || vehicleImageUrl(element.imagename)"
							:height="field.height || 200"
							:width="field.width || field.height || 200"
							style="object-fit: cover;"
							class="rounded"
						>
						<v-icon
							class="delete-icon"
							:icon="mdiClose"
							@click="removeImage(index)"
						/>
					</div>
				</template>
			</draggable>
			<v-alert v-if="!(modelValue && modelValue.length)">
				{{ field.empty || 'None taken' }}
			</v-alert>
		</div>
	</v-card-text>
</v-card>
</template>

<style lang="scss" scoped>
.image-container {
  position: relative;
  display: inline-block;
  animation: fadeIn 0.3s ease-in-out;

  img {
    display: block;
    border-radius: 8px;
    transition: opacity 0.3s;
  }

  .delete-icon {
    position: absolute;
    top: -4px;
    right: -4px;
    background-color: red;
    color: white;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }
}

@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
</style>